import { FC, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
	Alert,
	Box,
	Button,
	ButtonDropdown,
	Container,
	ContentLayout,
	Grid,
	Header,
	SpaceBetween,
	ColumnLayout,
	RadioGroup
} from '@cloudscape-design/components'

import HeaderWithStatusBadges from '../../../components/Common/HeaderWithStatusBadges'
import ApprovalDetails from '../../../components/Microservice/ApprovalDetails'
import ApprovalForm from '../../../components/Microservice/ApprovalForm'
import RejectionForm from '../../../components/Microservice/RejectionForm'
import LoadingSpinner from '../../../components/LoadingSpinner'
import CONSTANTS from '../../../constants'
import RequiredPermissions from '../../../helper/RequiredPermissions'
import CommentsOverview from '../../../components/Microservice/CommentsOverview'
import RenderMicroserviceFields from './RenderMicroserviceFields'
import { Microservice } from '../../../types/microservices'
import useUserPermissions from '../../../hooks/query/useUserPermissions'
import AccessDetails from '../../../components/Microservice/AccessDetails'
import ReadGenericTags from '../../../components/ControlledFormInputs/GenericTagsReadGCP'
// import EditGenericTags from '../../../components/ControlledFormInputs/GenericTagsEditGCP'
import useSingleMicroservice from '../../../hooks/query/useSingleMicroservice'
import ApproverOverview from '../../../components/ControlledFormInputs/ApproverOverview'

interface ApplicationNameType {
	label?: string;
	value?: string;
}

const MicroserviceDetails: FC = () => {
	const [selectedValue, setSelectedValue] = useState('default');
	const CURRENT_USER_ID = JSON.parse(localStorage['okta-token-storage'])[
		'idToken'
	]['claims']['email']
	const { id } = useParams() as { id: string }
	const navigate = useNavigate()
	const { data: thisMicroservice, isLoading } = useSingleMicroservice(id)
	const thisMicroserviceID = thisMicroservice?.microserviceID || ''
	const {
		data: userPermissions,
		isLoading: isLoadingPermissions,
		error: permissionsError
	} = useUserPermissions(CURRENT_USER_ID)
	const hasSpecificPermissions = (
		permissions: string[],
		requiredPermissions: string[]
	): boolean => {
		return requiredPermissions.some((requiredPerm) =>
			permissions.includes(requiredPerm)
		)
	}

	// Commenting to update here
	const specificPermissions = [
		'microservices:*:*',
		'microservices:*:approve',
		'*:*:*'
	]
	const hasRequiredPermissions =
		userPermissions &&
		hasSpecificPermissions(userPermissions.permissions, specificPermissions)
	const [showApprovalForm, setShowApprovalForm] = useState(false)
	const [showRejectionForm, setShowRejectionForm] = useState(false)
	const [showButton, setShowButton] = useState(true)
	function handleShowAccessDetails(): void {
		setShowButton(!showButton)
	}

	if (!thisMicroservice || isLoading) return <LoadingSpinner />

	if (thisMicroservice.status === 'ERROR') {
		return (
			<Alert
				statusIconAriaLabel="Error"
				type="error"
				header="An error occurred"
			>
				{thisMicroservice.details.message}
			</Alert>
		)
	}
	const handleApproveClick = () => {
		setShowApprovalForm(true)
		setShowRejectionForm(false)
	}

	const handleRejectClick = () => {
		setShowRejectionForm(true)
		setShowApprovalForm(false)
	}
	const microserviceVersionedObject = getKeyValueMicroservice(thisMicroservice)

	return (
		<ContentLayout
			header={
				<HeaderWithStatusBadges
					headerName={thisMicroservice?.microserviceName}
					status={thisMicroservice?.status}
					navigateTo='/microservices'
				/>
			}
		>
			<Grid
				gridDefinition={[
					{ colspan: { default: 3, xxs: 9 } },
					{ colspan: { default: 9, xxs: 3 } }
				]}
			>
				<SpaceBetween direction="vertical" size="l">
					<RenderMicroserviceFields
						Microservice={microserviceVersionedObject}
					/>

					{/* Commenting to update here */}
					{!hasRequiredPermissions &&
						thisMicroservice.status === 'PENDING_APPROVAL' && (
							<RequiredPermissions
								requiredPermissions={CONSTANTS.MICROSERVICE_REQUESTOR}
							>
								{/* <Container header={<Header variant="h3">Tag Details</Header>}>
									<div>{thisMicroservice.applicationName}'hello'</div>
									{thisMicroservice.tagsID && (
										<ReadGenericTags tagsId={thisMicroservice.tagsID} />
									)}
								</Container> */}
							</RequiredPermissions>
						)}

					{/* Commenting to update here */}
					{(thisMicroservice.status === 'APPROVED' ||
						thisMicroservice.status === 'REJECTED') && (
						<RequiredPermissions
							requiredPermissions={CONSTANTS.MICROSERVICE_REQUESTOR}
						>
							{/* <Container header={<Header variant="h3">Tag Details</Header>}>
								{thisMicroservice.tagsID && (
									<ReadGenericTags tagsId={thisMicroservice.tagsID} />
								)}
							</Container> */}
						</RequiredPermissions>
					)}
					{/* Commenting to update here */}
					{thisMicroservice.status === 'PENDING_APPROVAL' &&
						CURRENT_USER_ID !== thisMicroservice.requestorID && (
							<RequiredPermissions
								requiredPermissions={CONSTANTS.MICROSERVICE_APPROVER}
							>
								<Box float="left">
									<ButtonDropdown
										items={
											// isAllTagsApproved
											// 	? 
												[
														{ text: 'To Approve', id: 'to-approve' },
														{ text: 'To Reject', id: 'to-reject' }
												  ]
												// : [{ text: 'To Reject', id: 'to-reject' }]
										}
										onItemClick={({ detail }) => {
											if (detail.id === 'to-approve') {
												setShowApprovalForm(true)
												setShowRejectionForm(false)
											}

											if (detail.id === 'to-reject') {
												setShowApprovalForm(false)
												setShowRejectionForm(true)
											}
										}}
										variant="primary"
									>
										Action
									</ButtonDropdown>
								</Box>
							</RequiredPermissions>
						)}
					{showApprovalForm && (
						<ApprovalForm
							thisMicroserviceID={thisMicroserviceID}
							shouldShowApprovalForm={setShowApprovalForm}
						/>
					)}

					{showRejectionForm && (
						<RejectionForm
							thisMicroserviceID={thisMicroserviceID}
							shouldShowRejectionForm={setShowRejectionForm}
						/>
					)}
					{/* {thisMicroservice.status === 'APPROVED' && (
						<RequiredPermissions
							requiredPermissions={CONSTANTS.MICROSERVICE_APPROVER}
						>
							<ApprovalDetails approvalDetails={thisMicroservice!} />
						</RequiredPermissions>
					)} */}

					{thisMicroservice.status === 'APPROVED' &&
						CURRENT_USER_ID !== thisMicroservice.requestorID &&
						showButton && (
							<RequiredPermissions
								requiredPermissions={CONSTANTS.APPLICATION_APPROVER}
							>
								<Button variant="primary" onClick={handleShowAccessDetails}>
									View Access Details
								</Button>
							</RequiredPermissions>
						)}

					{!showButton && (
						<AccessDetails
							microserviceName={thisMicroservice?.microserviceName}
							applicationName={{
								label: (thisMicroservice?.applicationName as ApplicationNameType)?.label || '',
								value: (thisMicroservice?.applicationName as ApplicationNameType)?.value || ''
							}}
						/>
					)}
				</SpaceBetween>

				<SpaceBetween direction="vertical" size="l">
					<Container header={<Header variant="h3">Comment History</Header>}>
						<CommentsOverview comments={thisMicroservice.comments} />
					</Container>
				</SpaceBetween>
			</Grid>
		</ContentLayout>
	)
}

const getKeyValueMicroservice = (microservice: Microservice) => {
	const baseEntries = [
		{
			label: 'Requestor',
			value: microservice?.requestorID,
			version: getVersionStr(microservice?.version, 'requestorID')
		},
		{
			label: 'Microservice Name',
			value: microservice?.microserviceName,
			version: getVersionStr(microservice?.version, 'microserviceName')
		},
		{
			label: 'Application Name',
			value: (microservice?.applicationName as ApplicationNameType)?.label || 
				   (microservice?.applicationName as ApplicationNameType)?.value || '',
			version: getVersionStr(microservice?.version, 'applicationName')
		},
		{
			label: 'Code Template',
			value: microservice?.codeTemplate,
			version: getVersionStr(microservice?.version, 'codeTemplate')
		},
		{
			label: 'Application URL',
			value: microservice?.applicationUrl,
			version: getVersionStr(microservice?.version, 'applicationUrl')
		},
		{
			label: 'Port Number',
			value: microservice?.portNumber,
			version: getVersionStr(microservice?.version, 'portNumber')
		},
		{
			label: 'Microservice Endpoint',
			value: microservice?.microserviceEndpoint,
			version: getVersionStr(microservice?.version, 'microserviceEndpoint')
		},
		{
			label: 'WBS Code',
			value:
				microservice?.wbsCode === '' ? 'N/A' : microservice?.wbsCode,
			version: getVersionStr(microservice?.version, 'wbsCode')
		},
		{
			label: 'Cost Center',
			value:
				microservice?.costCenter === ''
					? 'N/A'
					: microservice?.costCenter,
			version: getVersionStr(microservice?.version, 'costCenter')
		}
	]
	return baseEntries
}

const getVersionStr = (version: Array<any> | undefined, key: string) => {
	let versionItems: string[] = []
	version?.forEach((element) => {
		if (!element[key]) return
		if (key === 'region' && element.region) {
			versionItems.unshift(`v${element.versionID}: ${element.region[0].label}`)
		} else {
			versionItems.unshift(`v${element.versionID}: ${element[key]}`)
		}
	})
	return versionItems
}

export default MicroserviceDetails
